import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { hasCapability, type Capability } from '@bre-hub/contracts'
import { api, type Me } from './api'
import { applyTheme } from './theme'

const SessionContext = createContext<Me | null>(null)

export function SessionProvider({ children, fallback }: { children: (me: Me) => ReactNode; fallback: ReactNode }) {
  const { data } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 })
  // The Hub record is the truth about the theme; the browser's copy only
  // covers the moment before this answer arrives (ADR 0012).
  const theme = data?.theme
  const loaded = !!data
  useEffect(() => {
    if (loaded) applyTheme(theme)
  }, [loaded, theme])
  if (!data) return <>{fallback}</>
  return <SessionContext.Provider value={data}>{children(data)}</SessionContext.Provider>
}

export function useMe(): Me {
  const me = useContext(SessionContext)
  if (!me) throw new Error('useMe outside SessionProvider')
  return me
}

export function useCan(capability: Capability): boolean {
  return hasCapability(useMe().capabilities, capability)
}

// Any one of several capabilities (one hook call, whatever the answer).
export function useCanAny(...capabilities: Capability[]): boolean {
  const held = useMe().capabilities
  return capabilities.some((capability) => hasCapability(held, capability))
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() || '?'
}

export function formatDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return withTime
    ? date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
